/**
 * Lost-item drill-down dialog.
 *
 * Shows the packer's evidence photo, the barcode-level unit trail, order
 * context, and the approval timeline. Approve/reject live here rather than on
 * the list row because deciding without seeing the photo and the tagged units
 * is how bad write-offs get waved through.
 *
 * Mounted once via {@link LostItemDetailDialogRoot} inside `AppShell`.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ImageOff, PackageX, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAccess } from '@/features/AccessProvider';
import {
  LOST_ITEM_REASON_LABEL,
  LOST_ITEM_STATUS_TONE,
  lostItemImageUrl,
  useLostItemDecision,
  useLostItemDetail,
  type LostItemDetail,
} from '@/api/bi/lostItems';
import {
  closeLostItemDetail,
  useLostItemDetailDialogStore,
} from '@/state/lostItemDetailDialog';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const UNIT_STATUS_TONE: Record<string, 'neutral' | 'success' | 'danger' | 'warning'> = {
  available: 'success',
  lost: 'danger',
  sold: 'neutral',
  damaged: 'warning',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xs">{children}</div>
    </div>
  );
}

function EvidencePhoto({ item }: { item: LostItemDetail }) {
  const [failed, setFailed] = useState(false);
  const url = lostItemImageUrl(item.image);

  if (!url || failed) {
    return (
      <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
        <ImageOff className="mr-1.5 h-4 w-4" />
        {url ? 'Photo could not be loaded' : 'No photo attached'}
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img
        src={url}
        alt={`Evidence for lost item #${item.id}`}
        onError={() => setFailed(true)}
        className="h-40 w-full rounded-md border border-border object-cover"
      />
    </a>
  );
}

/**
 * Reported → decided → written off. The third step is separate because an
 * approved report whose stock was never debited never reaches the ledger, so
 * the loss stays invisible in Count-vs-Book.
 */
function Timeline({ item }: { item: LostItemDetail }) {
  const steps: Array<{ label: string; at: string | null; by: string | null; done: boolean }> = [
    {
      label: 'Reported',
      at: item.reported_at,
      by: item.reported_by?.name ?? null,
      done: true,
    },
    {
      label:
        item.approval_status === 'rejected'
          ? 'Rejected'
          : item.approval_status === 'approved'
            ? 'Approved'
            : 'Awaiting decision',
      at: item.approved_at,
      by: item.approved_by?.name ?? null,
      done: item.approval_status !== 'pending',
    },
    {
      label: item.applied_to_stock ? 'Written off stock' : 'Not written off',
      at: item.applied_at,
      by: null,
      done: item.applied_to_stock,
    },
  ];

  return (
    <ol className="space-y-2">
      {steps.map((s) => (
        <li key={s.label} className="flex items-start gap-2">
          <span
            className={cn(
              'mt-1 h-1.5 w-1.5 shrink-0 rounded-full',
              s.done ? 'bg-success' : 'bg-muted-foreground/40',
            )}
          />
          <div className="min-w-0">
            <div className="text-xs font-medium">{s.label}</div>
            <div className="text-[11px] text-muted-foreground">
              {s.at ? fmt.datetime(s.at) : '—'}
              {s.by ? ` · ${s.by}` : ''}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function DecisionBar({ item }: { item: LostItemDetail }) {
  const { can } = useAccess();
  const decision = useLostItemDecision();
  const canManage = can('inventory.lost-items.queue', 'manage');

  if (!canManage) {
    return (
      <p className="text-[11px] text-muted-foreground">
        You have read-only access to the lost-item queue.
      </p>
    );
  }

  const isApproved = item.approval_status === 'approved';
  const isRejected = item.approval_status === 'rejected';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={() => decision.mutate({ id: item.id, action: 'approve' })}
          disabled={decision.isPending || isApproved}
        >
          <Check className="mr-1 h-3.5 w-3.5" />
          {isApproved ? 'Approved' : 'Approve'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => decision.mutate({ id: item.id, action: 'reject' })}
          disabled={decision.isPending || isRejected}
        >
          <X className="mr-1 h-3.5 w-3.5" />
          {isRejected ? 'Rejected' : 'Reject'}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {isApproved
          ? 'Rejecting restores the units to available stock and reverses the write-off.'
          : 'Approving writes the net missing units off stock and posts a ledger entry.'}
      </p>
      {decision.isError ? (
        <p className="text-[11px] text-danger">
          Could not save the decision. Try again.
        </p>
      ) : null}
    </div>
  );
}

function Body({ lostItemId }: { lostItemId: number }) {
  const detail = useLostItemDetail(lostItemId);

  if (detail.isLoading) {
    return (
      <div className="space-y-3 p-5">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="p-5">
        <ErrorState
          description="Could not load this lost-item report."
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const item = detail.data;
  const thumb = lostItemImageUrl(item.product_image);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-start gap-3 border-b border-border px-5 py-4">
        {thumb ? (
          <img
            src={thumb}
            alt=""
            className="h-10 w-10 shrink-0 rounded border border-border object-cover"
          />
        ) : (
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded border border-border text-muted-foreground">
            <PackageX className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-sm font-semibold">
              {item.product_name ?? `Product #${item.product_id ?? '—'}`}
            </h2>
            <Badge tone={LOST_ITEM_STATUS_TONE[item.approval_status]}>
              {item.approval_status}
            </Badge>
            <Badge tone="neutral">
              {LOST_ITEM_REASON_LABEL[item.reason] ?? item.reason}
            </Badge>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Report #{item.id}
            {item.store ? ` · ${item.store.name}` : ''}
            {item.order_id ? ` · Order #${item.order_id}` : ' · Floating loss'}
          </p>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <EvidencePhoto item={item} />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Reported lost">{fmt.number(item.quantity)}</Field>
              <Field label="Found">{fmt.number(item.found_quantity)}</Field>
              <Field label="Net missing">
                <span className="font-medium">{fmt.number(item.net_units)}</span>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Unit cost">
                {item.unit_cost != null ? fmt.npr(item.unit_cost) : 'Unknown'}
              </Field>
              <Field label="Estimated loss">
                {item.estimated_value != null ? fmt.npr(item.estimated_value) : 'Unknown'}
              </Field>
            </div>
            {item.notes ? (
              <Field label="Notes">
                <p className="whitespace-pre-wrap text-muted-foreground">{item.notes}</p>
              </Field>
            ) : null}
          </div>

          <div className="space-y-4">
            <section>
              <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                Approval timeline
              </h3>
              <Timeline item={item} />
            </section>

            {item.order ? (
              <section>
                <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  Order context
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Order">
                    <Link
                      to={`/orders/${item.order.id}`}
                      className="text-primary hover:underline"
                    >
                      #{item.order.id}
                    </Link>
                  </Field>
                  <Field label="Order status">{item.order.status}</Field>
                  <Field label="Ordered qty">
                    {item.order.order_item
                      ? fmt.number(item.order.order_item.quantity)
                      : '—'}
                  </Field>
                  <Field label="Order total">{fmt.npr(item.order.final_total)}</Field>
                </div>
              </section>
            ) : null}

            {item.ledger_entry ? (
              <section>
                <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  Stock ledger write-off
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Units out">{fmt.number(item.ledger_entry.qty_out)}</Field>
                  <Field label="Balance after">
                    {fmt.number(item.ledger_entry.balance)}
                  </Field>
                  <Field label="Posted">{fmt.datetime(item.ledger_entry.ts)}</Field>
                  <Field label="Reference">
                    <span className="font-mono text-[10px]">
                      {item.ledger_entry.reference}
                    </span>
                  </Field>
                </div>
              </section>
            ) : item.approval_status === 'approved' ? (
              <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-[11px] text-warning-soft">
                Approved but no ledger write-off found. The loss will not show in
                Count-vs-Book until stock is debited.
              </p>
            ) : null}

            <section>
              <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                Tagged units ({item.tags.length})
              </h3>
              {item.tags.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  No unit barcodes recorded on this report.
                </p>
              ) : (
                <div className="max-h-52 overflow-y-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted/60 text-[10px] uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-1.5 text-left">Barcode</th>
                        <th className="px-3 py-1.5 text-left">Unit status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.tags.map((t) => (
                        <tr key={t.id} className="border-t border-border">
                          <td className="px-3 py-1.5 font-mono text-[10px]">{t.tag}</td>
                          <td className="px-3 py-1.5">
                            {t.unit_status ? (
                              <Badge tone={UNIT_STATUS_TONE[t.unit_status] ?? 'neutral'}>
                                {t.unit_status}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      <footer className="border-t border-border px-5 py-3">
        <DecisionBar item={item} />
      </footer>
    </div>
  );
}

export function LostItemDetailDialogRoot() {
  const open = useLostItemDetailDialogStore((s) => s.open);
  const target = useLostItemDetailDialogStore((s) => s.target);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeLostItemDetail();
      }}
    >
      <DialogContent
        className={cn(
          'flex max-h-[min(90vh,900px)] w-[calc(100vw-1.5rem)] max-w-4xl flex-col overflow-hidden p-0',
          'gap-0 sm:max-w-4xl',
        )}
      >
        <DialogTitle className="sr-only">Lost item detail</DialogTitle>
        {target ? <Body key={target.lostItemId} lostItemId={target.lostItemId} /> : null}
      </DialogContent>
    </Dialog>
  );
}
